<template>
  <div class="success-page">
    <div class="success-card">
      <div class="signup-stepper">
        <div class="signup-step signup-step-done">
          <div class="signup-step-circle">&#10003;</div>
          <div class="signup-step-label">Account</div>
        </div>
        <div class="signup-step-line signup-step-line-done"></div>
        <div class="signup-step signup-step-done">
          <div class="signup-step-circle">&#10003;</div>
          <div class="signup-step-label">Market</div>
        </div>
        <div class="signup-step-line signup-step-line-done"></div>
        <div class="signup-step signup-step-done">
          <div class="signup-step-circle">&#10003;</div>
          <div class="signup-step-label">Billing</div>
        </div>
      </div>

      <div class="success-icon" aria-hidden="true">&#10003;</div>
      <h1 class="success-title">Subscription activated</h1>
      <p class="success-body">
        Your Kiey Market Exclusivity subscription is active and your market territory is locked
        in. Welcome aboard.
      </p>

      <!-- Confirmation card — what was billed, when the next charge lands, which market it
           covers. We pull these from billing_info if we can fetch it (auth-required), and
           fall back to generic copy when the user isn't yet authenticated on this redirect
           (Stripe round-trip can land before our session is restored). -->
      <div v-if="show_summary" class="confirmation-card">
        <div class="confirmation-row" v-if="paid_label">
          <div class="confirmation-label">Paid today</div>
          <div class="confirmation-value">{{ paid_label }}</div>
        </div>
        <div class="confirmation-row" v-if="next_renewal_label">
          <div class="confirmation-label">Next billing date</div>
          <div class="confirmation-value">{{ next_renewal_label }}</div>
        </div>
        <div class="confirmation-row" v-if="market_label">
          <div class="confirmation-label">Market</div>
          <div class="confirmation-value">{{ market_label }}</div>
        </div>
      </div>

      <p class="success-sub">
        We&rsquo;ll redirect you in a moment&hellip;
      </p>

      <div class="success-actions">
        <button
          type="button"
          class="success-btn success-btn-primary"
          @click="continueNow"
        >
          Continue to Welcome
        </button>
        <router-link to="/owner/account" class="success-btn success-btn-secondary">
          View Account / Billing
        </router-link>
      </div>

      <p class="success-fineprint">
        A receipt was emailed to you by Stripe. You can also download your invoice anytime from
        Account &rarr; Billing.
      </p>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';

export default {
  name: 'BillingSuccess',
  data() {
    return {
      // Stripe redirects with ?session_id=cs_... — held here in case future
      // analytics or receipt-display work needs it.
      session_id: null,
      redirect_timer: null,
      // True once we've successfully pulled billing_info post-redirect.
      // Auth may not be restored yet on this hop (Stripe → admin.kiey.com),
      // so we render a generic confirmation if the fetch fails and never
      // block the user from continuing.
      info_loaded: false,
    };
  },
  computed: {
    ...mapState({
      billing_info: state => state.Market_billing.billing_info,
      my_market: state => state.Market_billing.my_market,
    }),
    show_summary() {
      // Render the confirmation card only when we have something concrete
      // to show — otherwise the empty card looks more confusing than not.
      return !!(this.paid_label || this.next_renewal_label || this.market_label);
    },
    paid_label() {
      // Pull the most-recent paid invoice from billing_info.invoices
      // (sorted desc by Stripe). If we don't have one yet (webhook + this
      // page can race), fall through to the calculated price from
      // billing_info.subscription.amount.
      const invoices = (this.billing_info && this.billing_info.invoices) || [];
      const lastPaid = invoices.find(inv => inv.status === 'paid');
      if (lastPaid && typeof lastPaid.amount_paid === 'number') {
        return this.format_currency(lastPaid.amount_paid, lastPaid.currency);
      }
      const sub = this.billing_info && this.billing_info.subscription;
      if (sub && typeof sub.amount === 'number') {
        return this.format_currency(sub.amount, sub.currency || 'usd');
      }
      return '';
    },
    next_renewal_label() {
      const sub = this.billing_info && this.billing_info.subscription;
      if (sub && sub.current_period_end) {
        return this.format_date(sub.current_period_end);
      }
      return '';
    },
    market_label() {
      if (this.my_market && Array.isArray(this.my_market.zip_codes) && this.my_market.zip_codes.length) {
        const n = this.my_market.zip_codes.length;
        return `${n} ZIP${n === 1 ? '' : 's'}`;
      }
      return '';
    },
  },
  mounted() {
    this.session_id = this.$route.query.session_id || null;
    // Fire-and-forget — billing_info enriches the confirmation card. If
    // the user isn't authed (post-Stripe redirect can land before the
    // session restores), we just render the generic copy.
    this.$store.dispatch('Market_billing/fetch_billing_info').then(() => {
      this.info_loaded = true;
    }).catch(() => {});
    this.$store.dispatch('Market_billing/fetch_markets').catch(() => {});
    // Auto-route to /sign_up/welcome after 5s. Cleared if the user
    // navigates away or clicks Continue manually.
    this.redirect_timer = setTimeout(() => {
      this.continueNow();
    }, 5000);
  },
  beforeDestroy() {
    if (this.redirect_timer) {
      clearTimeout(this.redirect_timer);
      this.redirect_timer = null;
    }
  },
  methods: {
    continueNow() {
      if (this.redirect_timer) {
        clearTimeout(this.redirect_timer);
        this.redirect_timer = null;
      }
      this.$router.push({ name: 'sign_up.welcome' });
    },
    format_currency(amount, currency) {
      if (typeof amount !== 'number' || amount <= 0) return '';
      try {
        return amount.toLocaleString('en-US', {
          style: 'currency',
          currency: (currency || 'usd').toUpperCase(),
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      } catch (e) {
        return `$${amount.toFixed(2)}`;
      }
    },
    format_date(ts) {
      if (!ts) return '';
      // billing_info encodes timestamps as ms. Be defensive.
      const ms = typeof ts === 'number' ? ts : Date.parse(ts);
      if (!ms || isNaN(ms)) return '';
      return new Date(ms).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    },
  },
};
</script>

<style scoped>
.success-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
  box-sizing: border-box;
  background-color: #f5f7fb;
}
.success-card {
  width: 100%;
  max-width: 600px;
  background-color: #ffffff;
  border-radius: 12px;
  padding: 48px 40px;
  text-align: center;
  box-shadow: 0 8px 24px rgba(6, 29, 71, 0.08);
  box-sizing: border-box;
}

/* Stepper */
.signup-stepper {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 32px;
  max-width: 360px;
}
.signup-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 88px;
}
.signup-step-circle {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #e4e8f1;
  color: #5a6478;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
}
.signup-step-done .signup-step-circle { background: #19a99d; color: #fff; }
.signup-step-label {
  font-size: 11px;
  color: #5a6478;
  margin-top: 4px;
  letter-spacing: 0.3px;
  font-weight: 600;
  text-transform: uppercase;
}
.signup-step-done .signup-step-label { color: #19a99d; }
.signup-step-line {
  flex: 0 1 80px;
  height: 2px;
  background: #19a99d;
  margin: 0 6px 18px;
}

/* Hero */
.success-icon {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: #19a99d;
  color: #ffffff;
  font-size: 36px;
  font-weight: 700;
  margin: 8px auto 22px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.success-title {
  color: #061d47;
  font-family: 'Roboto-Medium', Arial, sans-serif;
  font-size: 30px;
  font-weight: 600;
  line-height: 1.25;
  margin: 0 0 14px 0;
}
.success-body {
  color: #4a4a4a;
  font-family: Arial, sans-serif;
  font-size: 16px;
  line-height: 1.5;
  margin: 0 0 24px 0;
}

/* Confirmation card */
.confirmation-card {
  text-align: left;
  background: #fafbfd;
  border: 1px solid #e4e8f1;
  border-radius: 10px;
  padding: 18px 22px;
  margin: 0 0 24px;
}
.confirmation-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  padding: 6px 0;
  font-size: 14px;
}
.confirmation-row + .confirmation-row {
  border-top: 1px solid #f0f2f6;
}
.confirmation-label {
  color: #6a7388;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.4px;
  text-transform: uppercase;
}
.confirmation-value {
  color: #061d47;
  font-size: 15px;
  font-weight: 600;
}

.success-sub {
  color: #6a6a6a;
  font-family: Arial, sans-serif;
  font-size: 13px;
  line-height: 1.5;
  margin: 0 0 24px 0;
}

/* Actions */
.success-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
  margin-bottom: 18px;
}
.success-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 48px;
  padding: 0 28px;
  border-radius: 24px;
  font-family: Arial, sans-serif;
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  border: 1px solid transparent;
  box-sizing: border-box;
  min-width: 200px;
}
.success-btn-primary {
  background: #29abe2;
  color: #ffffff;
}
.success-btn-primary:hover {
  background: #1f9bd0;
}
.success-btn-secondary {
  background: #ffffff;
  color: #061d47;
  border-color: #061d47;
}
.success-btn-secondary:hover {
  background: #061d47;
  color: #ffffff;
}

.success-fineprint {
  color: #6a7388;
  font-size: 12px;
  line-height: 1.5;
  margin: 12px 0 0;
}

@media (max-width: 600px) {
  .success-card { padding: 32px 20px; }
  .signup-stepper { max-width: 280px; margin-bottom: 24px; }
  .signup-step { min-width: 76px; }
  .signup-step-line { flex: 0 1 60px; }
  .signup-step-label { font-size: 10px; }
  .success-title { font-size: 24px; }
  .success-btn { width: 100%; min-width: 0; }
  .confirmation-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
  }
}
</style>
